import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { buildDrillDownStopState, ROUTES } from '@cyberint/common-utils';
import { Icons, IconSizes, SearchInput } from '@cyberint/dsc';
import { t } from '../../../i18n';
import * as S from './styled';

const HOVER_DELAY_MS = 300;

const GlobalSearchNavigation = ({ closeMenu }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const hoverTimer = useRef(null);

  const handleSearch = useCallback(
    value => {
      const path = value
        ? `/${ROUTES.GLOBAL_SEARCH_TAB}?${new URLSearchParams({ value }).toString()}`
        : `/${ROUTES.GLOBAL_SEARCH_TAB}`;
      navigate(path, { state: buildDrillDownStopState() });
      closeMenu();
    },
    [navigate, closeMenu]
  );

  const handleMouseEnter = useCallback(() => {
    hoverTimer.current = setTimeout(() => setIsExpanded(true), HOVER_DELAY_MS);
  }, []);

  const handleMouseLeave = useCallback(e => {
    clearTimeout(hoverTimer.current);
    if (!e.currentTarget.contains(document.activeElement)) {
      setIsExpanded(false);
    }
  }, []);

  const handleIconClick = useCallback(() => {
    clearTimeout(hoverTimer.current);
    navigate(`/${ROUTES.GLOBAL_SEARCH_TAB}`, { state: buildDrillDownStopState() });
    closeMenu();
  }, [navigate, closeMenu]);

  const handleBlur = useCallback(e => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsExpanded(false);
    }
  }, []);

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
    }
  }, []);

  useEffect(
    () => () => {
      clearTimeout(hoverTimer.current);
    },
    []
  );

  return (
    <S.GlobalSearchWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}>
      <S.SearchIconButton onClick={handleIconClick} data-testid="btn-global-search-icon">
        <Icons.Search size={IconSizes.M} />
      </S.SearchIconButton>
      {isExpanded && (
        <S.SearchPopup>
          <SearchInput
            placeholder={t('topbar.search_navigation_menu.placeholder')}
            onSearch={handleSearch}
            autoFocus
            data-testid="input-global-search"
          />
        </S.SearchPopup>
      )}
    </S.GlobalSearchWrapper>
  );
};

GlobalSearchNavigation.propTypes = {
  closeMenu: PropTypes.func.isRequired
};

export default GlobalSearchNavigation;
